import { useInfiniteQuery, useQuery, type QueryClient } from "@tanstack/react-query";
import {
  ADMIN_PRIVACY_REQUESTS_DEFAULT_LIMIT,
  AdminPrivacyRequestsResponse,
  DeletionImpact,
  DeletionStatusResponse,
  type DeletionRequestBody,
  type DeletionRequestState,
  type PrivacyRequestType,
} from "@townsy/contracts";
import { API_BASE, ApiError, apiFetch, isApiError } from "./client";

/**
 * Privacy (ADR-0037): the signed-in account's data export and deletion request, and the staff list of every request
 * (GET /admin/privacy-requests, privacy.requests.manage; the server checks the permission on every request).
 */
export const privacyEndpoints = {
  export: `${API_BASE}/me/privacy/export`,
  impact: `${API_BASE}/me/privacy/deletion-impact`,
  deletion: `${API_BASE}/me/privacy/deletion`,
  cancel: `${API_BASE}/me/privacy/deletion/cancel`,
  adminList: `${API_BASE}/admin/privacy-requests`,
};

/** Applied staff-list filters, exactly the query parameters sent (empty = every request). */
export interface PrivacyRequestFilters {
  type?: PrivacyRequestType;
  state?: DeletionRequestState;
}

export const privacyKeys = {
  all: ["privacy"] as const,
  impact: ["privacy", "impact"] as const,
  status: ["privacy", "deletion"] as const,
  adminList: (filters: PrivacyRequestFilters) => ["privacy", "admin-list", filters] as const,
};

export function privacyRequestsSearch(filters: PrivacyRequestFilters, cursor: string | null, limit = ADMIN_PRIVACY_REQUESTS_DEFAULT_LIMIT): string {
  const q = new URLSearchParams();
  if (filters.type) q.set("type", filters.type);
  if (filters.state) q.set("state", filters.state);
  if (cursor) q.set("cursor", cursor);
  q.set("limit", String(limit));
  return q.toString();
}

export const fetchPrivacyRequests = (filters: PrivacyRequestFilters, cursor: string | null, signal?: AbortSignal) =>
  apiFetch(`${privacyEndpoints.adminList}?${privacyRequestsSearch(filters, cursor)}`, { schema: AdminPrivacyRequestsResponse, signal });

export const fetchDeletionImpact = (): Promise<DeletionImpact> => apiFetch(privacyEndpoints.impact, { schema: DeletionImpact });
export const fetchDeletionStatus = (): Promise<DeletionStatusResponse> => apiFetch(privacyEndpoints.deletion, { schema: DeletionStatusResponse });
export const requestDeletion = (body: DeletionRequestBody, idempotencyKey: string): Promise<DeletionStatusResponse> =>
  apiFetch(privacyEndpoints.deletion, { method: "POST", body, headers: { "idempotency-key": idempotencyKey }, schema: DeletionStatusResponse });
export const cancelDeletion = (): Promise<DeletionStatusResponse> => apiFetch(privacyEndpoints.cancel, { method: "POST", body: {}, schema: DeletionStatusResponse });

const noAuthRetry = (count: number, err: unknown) => !(isApiError(err) && [401, 403, 404].includes(err.status)) && count < 2;

/** Staff: the privacy request list with "Load more" (cursor pages). */
export function usePrivacyRequestsQuery(filters: PrivacyRequestFilters, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: privacyKeys.adminList(filters),
    queryFn: ({ pageParam, signal }) => fetchPrivacyRequests(filters, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    retry: (count, err) => !(isApiError(err) && [401, 403, 404, 422].includes(err.status)) && count < 2,
  });
}

export function useDeletionImpactQuery(enabled: boolean) {
  return useQuery({ queryKey: privacyKeys.impact, queryFn: fetchDeletionImpact, enabled, staleTime: 0, retry: noAuthRetry });
}

/** The pending deletion request (the banner). Polled slowly while one is pending, so a processed delay shows. */
export function useDeletionStatusQuery(enabled: boolean) {
  return useQuery({
    queryKey: privacyKeys.status,
    queryFn: fetchDeletionStatus,
    enabled,
    staleTime: 30_000,
    refetchInterval: (query) => (enabled && query.state.data?.request ? 5 * 60_000 : false),
    retry: noAuthRetry,
  });
}

export async function invalidatePrivacy(qc: QueryClient): Promise<void> {
  await Promise.all([qc.invalidateQueries({ queryKey: privacyKeys.all }), qc.invalidateQueries({ queryKey: ["me"] })]);
}

/** A fresh Idempotency-Key for one deletion request (kept while the same form is retried). */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) return `del-${c.randomUUID()}`;
  return `del-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** The filename the server suggests (content-disposition), or a dated fallback. */
export function exportFilename(contentDisposition: string | null, now = new Date()): string {
  const m = contentDisposition ? /filename="([^"]+)"/.exec(contentDisposition) : null;
  const name = m?.[1] ?? "";
  return /^[\w.-]{1,80}$/.test(name) ? name : `townsy-data-${now.toISOString().slice(0, 10)}.json`;
}

/**
 * GET /me/privacy/export and save it as a file. Errors come back as ApiError (429 when the daily limit is used up).
 * Uses the browser's own download: a temporary object URL, revoked right after.
 */
export async function downloadMyData(): Promise<string> {
  let res: Response;
  try {
    res = await fetch(privacyEndpoints.export, { credentials: "include", headers: { accept: "application/json" } });
  } catch {
    throw new ApiError("DEPENDENCY_UNAVAILABLE", "Can't reach Townsy right now. Check your connection.", { status: 0, retryable: true });
  }
  if (!res.ok) {
    let message = res.status === 429 ? "You downloaded your data several times already. Please try again later." : `The download failed (${res.status}).`;
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } };
      if (body.error?.message) message = body.error.message;
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status === 429 ? "RATE_LIMITED" : res.status === 401 ? "UNAUTHENTICATED" : "INTERNAL", message, { status: res.status, retryable: res.status === 429 });
  }
  const blob = await res.blob();
  const name = exportFilename(res.headers.get("content-disposition"));
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return name;
}
